import React, { useState, useMemo } from 'react';

/**
 * Универсальный табличный компонент с фильтрацией и сортировкой
 */
export default function DataTable({
                                    data = [],
                                    columns = null,
                                    options = {}
                                  }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filter, setFilter] = useState('');
  const {
    showCheckboxes = false,
    showRowNumbers = true,
    striped = true,
    bordered = true,
    hoverable = true,
    sortable = true,
    filterable = true,
    // height можно передавать, но мы полагаемся на flex-цепочку
    height = '100%',
    emptyMessage = 'Нет данных для отображения',
    onRowClick = null,
    onRowDoubleClick = null,
    className = '',
    selectedRow = null,
    clickableRows = false
  } = options;

  // Автоматическое определение колонок из данных, если не переданы
  const tableColumns = useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (data.length === 0) return [];
    const allKeys = new Set();
    data.forEach(item => Object.keys(item).forEach(k => allKeys.add(k)));
    return Array.from(allKeys).map(key => ({
      key,
      title: key.charAt(0).toUpperCase() + key.slice(1),
      sortable: true,
      filterable: true
    }));
  }, [data, columns]);

  // Фильтрация данных
  const filteredData = useMemo(() => {
    if (!filter.trim()) return data;
    return data.filter(item =>
        tableColumns.some(column => String(item[column.key] ?? '').toLowerCase().includes(filter.toLowerCase()))
    );
  }, [data, filter, tableColumns]);

  // Сортировка данных
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let cmp = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        cmp = aValue - bValue;
      } else {
        cmp = String(aValue).localeCompare(String(bValue));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortField, sortDirection]);

  // Обработка клика по заголовку для сортировки
  const handleSort = (field) => {
    if (!sortable) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Обработка выбора строк
  const handleRowSelect = (index, checked) => {
    const next = new Set(selectedRows);
    if (checked) next.add(index); else next.delete(index);
    setSelectedRows(next);
  };
  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? new Set(sortedData.map((_, i) => i)) : new Set());
  };
  // Базовые стили
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '14px', backgroundColor: '#fff', tableLayout: 'auto' };
  const thBase = { padding: '12px 8px', borderBottom: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, backgroundColor: '#f8f9fa', color: '#495057', position: 'sticky', top: 0, zIndex: 1 };
  const tdBase = { padding: '12px 8px', borderBottom: '1px solid #e9ecef', textAlign: 'left' };

  return (
      <div
          style={{
            width: '100%',
            // НЕ задаём фиксированных высот тут — только flex-цепочка снаружи
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            boxSizing: 'border-box'
          }}
      >
        {/* Фильтр */}
        {filterable && (
            <div style={{ marginBottom: 16, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                  type="text"
                  placeholder="Поиск по таблице..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, outline: 'none' }}
                  onFocus={(e) => (e.target.style.borderColor = '#007bff')}
                  onBlur={(e) => (e.target.style.borderColor = '#ddd')}
              />
              <button
                  onClick={() => setFilter('')}
                  style={{
                    padding: '8px',
                    border: 'none',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#6c757d'
                  }}
              >
                ✖
              </button>
            </div>
        )}

        {/* Скроллируемая обёртка таблицы */}
        <div
            style={{
              flex: 1,
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              boxSizing: 'border-box',
              border: bordered ? '1px solid #ddd' : 'none',
              borderRadius: 8,
              backgroundColor: '#fff'
            }}
            className={className}
        >
          {sortedData.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6c757d', fontSize: 16 }}>
                {emptyMessage}
              </div>
          ) : (
              <table style={tableStyle}>
                <thead>
                <tr>
                  {showCheckboxes && (
                      <th style={{ ...thBase, width: 40 }}>
                        <input
                            type="checkbox"
                            checked={selectedRows.size === sortedData.length && sortedData.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                  )}
                  {showRowNumbers && <th style={{ ...thBase, width: 60 }}>#</th>}                  {tableColumns.map((column) => {
                    // Строим объект стилей с учетом ширины колонки
                    const columnStyle = { 
                      ...thBase, 
                      cursor: sortable && column.sortable !== false ? 'pointer' : 'default', 
                      userSelect: 'none'
                    };
                    
                    // Добавляем ширину из определения колонки
                    if (column.width) {
                      columnStyle.width = column.width;
                    }
                    if (column.minWidth) {
                      columnStyle.minWidth = column.minWidth;
                    }
                    if (column.maxWidth) {
                      columnStyle.maxWidth = column.maxWidth;
                    }
                    if (column.textAlign) {
                      columnStyle.textAlign = column.textAlign;
                    }
                    
                    return (
                      <th
                          key={column.key}
                          style={columnStyle}
                          onClick={() => sortable && column.sortable !== false && handleSort(column.key)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{column.title}</span>
                          {sortable && column.sortable !== false && sortField === column.key && (
                              <span style={{ fontSize: 12 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>                      </th>
                    );
                  })}
                </tr>
                </thead>
                <tbody>                {sortedData.map((row, index) => {
                  const isSelected = selectedRows.has(index);
                  const isHighlighted = selectedRow === index;
                  let bg = striped && index % 2 === 1 ? '#f8f9fa' : '#fff';
                  
                  // Если строка выделена, используем специальный цвет
                  if (isHighlighted) {
                    bg = '#cce5ff';
                  }
                  
                  return (
                      <tr
                          key={index}
                          style={{ 
                            backgroundColor: bg, 
                            cursor: (onRowClick || clickableRows) ? 'pointer' : 'default', 
                            transition: hoverable ? 'background-color 0.2s' : 'none',
                            borderLeft: isHighlighted ? '4px solid #007bff' : '4px solid transparent'
                          }}
                          onClick={() => onRowClick && onRowClick(row, index)}
                          onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row, index)}
                          onMouseEnter={(e) => { 
                            if (hoverable && !isHighlighted) {
                              e.currentTarget.style.backgroundColor = '#e9ecef'; 
                            }
                          }}
                          onMouseLeave={(e) => { 
                            if (hoverable) {
                              e.currentTarget.style.backgroundColor = bg; 
                            }
                          }}
                      >
                        {showCheckboxes && (
                            <td style={tdBase}>
                              <input type="checkbox" checked={isSelected} onChange={(e) => handleRowSelect(index, e.target.checked)} />
                            </td>
                        )}
                        {showRowNumbers && <td style={{ ...tdBase, color: '#6c757d' }}>{index + 1}</td>}                        {tableColumns.map((column) => {
                          const cellStyle = { ...tdBase };
                          
                          // Добавляем выравнивание текста из определения колонки
                          if (column.textAlign) {
                            cellStyle.textAlign = column.textAlign;
                          }
                          
                          return (
                            <td key={column.key} style={cellStyle}>
                              {column.render ? column.render(row[column.key], row, index) : (row[column.key] ?? '-')}
                            </td>
                          );
                        })}
                      </tr>
                  );
                })}
                </tbody>
              </table>
          )}
        </div>

        {/* Информация о данных */}
        {filteredData.length !== data.length && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#6c757d', textAlign: 'right', flexShrink: 0 }}>
              Показано {filteredData.length} из {data.length} записей
            </div>
        )}
      </div>
  );
}
